import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
} from '@angular/core';
import {
  DeviceDetectorService,
  FileDownloadService,
  IsLoadingService,
  RouteTrackingService,
  XAlertService,
} from '@core/services';
import { WorkflowService } from '@core/services/workflow/workflow.service';
import { RequestCommentsService } from '@core/services/request/request-comments.service';
import { RequestDealDetailsService } from '@core/services/request/request-deal-details.service';
import { RequestDevicesService } from '@core/services/request/request-devices.service';
import { RequestFilesService } from '@core/services/request/request-files.service';
import { RequestService } from '@core/services/request/request.service';
import { RequestSettingsService } from '@core/services/request/request-settings.service';
import { AsyncPipe } from '@angular/common';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  forkJoin,
  map,
  merge,
  mergeScan,
  Observable,
  of,
  shareReplay,
  switchMap,
} from 'rxjs';
import { OutkeepTranslateService } from '@core/services/outkeep-translate.service';
import {
  RequestFormSummaryDesktopComponent,
  RequestFormSummaryMobileComponent,
} from './components';
import {
  REQUEST_LIST_ROUTE_PATHS,
  RequestComment,
  RequestDealDetailsExtended,
  RequestDevice,
  RequestFile,
  RequestFileCategory,
  RequestFormDevicesAddDeviceTranslations,
  RequestFormDevicesAddDeviceTranslationsPayload,
  RequestFormFilesDownloadTranslations,
  RequestFormFilesDownloadTranslationsPayload,
  WorkflowsTranslations,
  WorkflowsTranslationsPayload,
} from '@core/types';
import { WA_WINDOW, WA_LOCATION } from '@ng-web-apis/common';
import { Nullable } from '@core/helpers';
import { DeviceInstallQualityCheckStatus } from '@core/types/DeviceInstallQuality';
import { ActivatedRoute } from '@angular/router';
import { FileGroup } from '@features/requests/request-form/components/request-form-summary/types';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { environment } from '@environment/environment';
import { formatInTimeZone } from 'date-fns-tz';
import { enGB, ru, tr } from 'date-fns/locale';
import { AccountService } from '@core/services/account/account.service';
import { OfflineSnapshotRepository } from '@core/services/offline/offline-snapshot.repository';
import type {
  OfflineViewState,
  RequestSummarySnapshot,
} from '@core/services/offline/offline-snapshot.types';
import { TranslatePipe } from '@ngx-translate/core';
import { isOfflineFallbackError } from '@core/services/offline/offline-fallback.helpers';

@Component({
  selector: 'x-request-form-summary',
  imports: [
    AsyncPipe,
    RequestFormSummaryDesktopComponent,
    RequestFormSummaryMobileComponent,
    TranslatePipe,
  ],
  templateUrl: './request-form-summary.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RequestFormSummaryComponent implements OnInit, OnDestroy {
  public readonly deviceService = inject(DeviceDetectorService);
  private readonly requestService = inject(RequestService);
  private readonly requestFilesService = inject(RequestFilesService);
  private readonly outkeepTranslateService = inject(OutkeepTranslateService);
  private readonly xAlertService = inject(XAlertService);
  private readonly requestSettingsService = inject(RequestSettingsService);
  private readonly requestCommentsService = inject(RequestCommentsService);
  private readonly requestDevicesService = inject(RequestDevicesService);
  private readonly dealDetailsService = inject(RequestDealDetailsService);
  private readonly workflowService = inject(WorkflowService);
  private readonly accountService = inject(AccountService);
  private readonly offlineSnapshotRepository = inject(
    OfflineSnapshotRepository,
  );
  public readonly activatedRoute = inject(ActivatedRoute);
  public readonly routeTrackingService = inject(RouteTrackingService);

  private readonly renderer: Renderer2 = inject(Renderer2);
  private readonly fileDownloadService = inject(FileDownloadService);
  readonly isLoading$: BehaviorSubject<boolean> = inject(IsLoadingService);
  private readonly destroyRef = inject(DestroyRef);
  readonly $requestNumberOverride = input<string | null>(null, {
    alias: 'requestNumber',
  });
  readonly $renderMode = input<'auto' | 'mobile'>('auto', {
    alias: 'renderMode',
  });

  // Create a signal from route params to safely access them in computed
  private readonly $routeParams = toSignal(
    this.activatedRoute.parent?.params || this.activatedRoute.params,
    { initialValue: {} },
  );

  readonly $request = computed(() => {
    const request = this.requestService.$request();
    const pageRequestNumber = this.getResolvedRequestNumber();

    // Only return the request if it matches the current route parameter (not to emit the old request from previous navigation)
    if (request?.requestNumber === pageRequestNumber) {
      return request;
    }

    return null;
  });

  private translates: Partial<
    RequestFormFilesDownloadTranslationsPayload &
      RequestFormDevicesAddDeviceTranslationsPayload &
      WorkflowsTranslationsPayload
  > = {
    [RequestFormFilesDownloadTranslations.ChecklistNotFound]:
      'Чек-лист не был загружен!',
    [RequestFormDevicesAddDeviceTranslations.DialogAddDevice]:
      'Вы действительно хотите добавить устройство',
    [RequestFormDevicesAddDeviceTranslations.DialogAddDeviceYes]:
      'Да, добавить',
    [RequestFormDevicesAddDeviceTranslations.DialogAddDeviceNo]: 'Отмена',
    [RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDateYes]: 'Да',
    [RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDateNo]:
      'Отмена',
    [WorkflowsTranslations.StartWorkFlowExecution]: 'Приступить',
    [WorkflowsTranslations.ContinueWorkFlowExecution]: 'Продолжить',
  };
  private readonly window = inject(WA_WINDOW);
  protected readonly location = inject(WA_LOCATION);

  public ePTS: Nullable<RequestFile> = null;
  private lPPhoto: Nullable<RequestFile> = null;
  private readonly requestDevicesForInstallQuality$ = new BehaviorSubject<
    Nullable<RequestDevice[]>
  >(null);

  public requestComments: Nullable<RequestComment[]> = null;
  public requestDevices: Nullable<RequestDevice[]> = null;
  public files: Nullable<RequestFile[]> = null;
  public fileCats: Nullable<RequestFileCategory[]> = null;
  public dealDetails: Nullable<RequestDealDetailsExtended> = null;
  public readonly devicesInstallQuality$: Observable<
    Nullable<
      Record<RequestDevice['deviceHash'], DeviceInstallQualityCheckStatus>
    >
  > = this.requestDevicesForInstallQuality$.pipe(
    switchMap(requestDevices => {
      if (!requestDevices) {
        return of(null);
      }

      return merge(
        of(null),
        ...requestDevices.map(device => {
          return this.requestDevicesService
            .listenToInstallQualityUpdates$(device.deviceHash)
            .pipe(map(() => device.deviceHash));
        }),
      ).pipe(
        mergeScan(
          (
            existingInstallQualityStatuses,
            deviceHash: RequestDevice['deviceHash'] | null,
          ) => {
            if (!requestDevices.length) {
              return of({});
            }

            return forkJoin(
              requestDevices.map(device => {
                const deviceStatus =
                  existingInstallQualityStatuses?.[device.deviceHash];

                if (
                  deviceStatus &&
                  deviceHash &&
                  deviceHash !== device.deviceHash
                ) {
                  return of([device.deviceHash, deviceStatus] as const);
                }

                return this.requestDevicesService
                  .getDeviceInstallQualityStatus$(device.deviceHash)
                  .pipe(map(status => [device.deviceHash, status] as const));
              }),
            ).pipe(
              map(
                entries =>
                  Object.fromEntries(entries) as Record<
                    RequestDevice['deviceHash'],
                    DeviceInstallQualityCheckStatus
                  >,
              ),
              catchError(err => {
                console.error(err);

                return of(existingInstallQualityStatuses);
              }),
            );
          },
          {} as Record<
            RequestDevice['deviceHash'],
            DeviceInstallQualityCheckStatus
          >,
        ),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  public moreFileCatsWithFilesCount = 0;
  public isInitialLoad = true;
  public locale = this.outkeepTranslateService.getLocaleStr();

  // Используем toSignal для автоматической подписки/отписки
  public readonly $workflowUrl = toSignal(
    this.workflowService.getStartWorkflowUrl$(),
    { initialValue: '' },
  );

  // Используем computed для вычисления текста кнопки на основе данных workflow
  public readonly $workflowUrlButtonText = computed(() => {
    const wfData = this.workflowService.$requestsWorkflowData();

    if (!wfData) return '';

    return wfData.submissions.length
      ? (this.translates[
          WorkflowsTranslations.ContinueWorkFlowExecution
        ] as string)
      : (this.translates[
          WorkflowsTranslations.StartWorkFlowExecution
        ] as string);
  });

  public readonly $requestWorkflowsExist =
    this.workflowService.requestWorkflowsExist;

  private previousUrl: string | null = null;
  private lastRequestNumber = signal<string | null>(null);
  private readonly _$offlineState = signal<OfflineViewState>('online');
  private readonly _$snapshotFetchedAt = signal<string | null>(null);
  readonly $offlineState = this._$offlineState.asReadonly();
  readonly $snapshotFetchedAt = this._$snapshotFetchedAt.asReadonly();
  readonly $isReadOnly = computed(
    () =>
      this.$offlineState() !== 'online' ||
      this.requestService.$requestOfflineState() !== 'online',
  );

  constructor() {
    effect(() => {
      const requestNumber = this.$requestNumberOverride();

      if (!requestNumber) {
        return;
      }

      this.requestService.loadRequest(requestNumber);
    });

    effect(() => {
      const request = this.$request();
      const pageRequestNumber = this.getResolvedRequestNumber();
      const reconnecting =
        this.$offlineState() === 'snapshot' &&
        this.requestService.$requestOfflineState() === 'online';

      if (
        request &&
        pageRequestNumber &&
        request.requestNumber === pageRequestNumber &&
        (this.lastRequestNumber() !== request.requestNumber || reconnecting)
      ) {
        this.lastRequestNumber.set(request.requestNumber);
        this.previousUrl = this.routeTrackingService.getPreviousUrl();
        this.getAllNeededEntities();
      }
    });

    // bindings
    this.formatDateWithLocale = this.formatDateWithLocale.bind(this);
    this.shareRequestLink = this.shareRequestLink.bind(this);
    this.getCheckList = this.getCheckList.bind(this);
    this.openYaMaps = this.openYaMaps.bind(this);
  }

  ngOnDestroy(): void {
    Object.assign(this, {
      translates: null,
      requestDevices: null,
      requestComments: null,
      files: null,
      fileCats: null,
    });
  }

  ngOnInit(): void {
    this.subscribeForLangChange();
  }

  isDesktopSummary(isDesktop: boolean | null): boolean {
    return this.$renderMode() !== 'mobile' && Boolean(isDesktop);
  }

  private getResolvedRequestNumber(): string | null {
    const requestNumberOverride = this.$requestNumberOverride();

    if (requestNumberOverride) {
      return requestNumberOverride;
    }

    const routeParams = this.$routeParams();

    return 'requestNumber' in routeParams ? routeParams['requestNumber'] : null;
  }

  public mainFileGroups: FileGroup[] = [
    { name: 'ПТС/ПСМ', id: 21, filesExists: false, multi: false, files: [] },
    {
      name: 'Образцы прочее',
      id: 15,
      filesExists: false,
      multi: false,
      files: [],
    },
    { name: 'АПП к ДКП', id: 18, filesExists: false, multi: false, files: [] },
    { name: 'АПП к ДЛ', id: 17, filesExists: false, multi: false, files: [] },
    {
      name: 'Доверенности Поставщика',
      id: 31,
      filesExists: false,
      multi: false,
      files: [],
    },
  ];

  private readonly mainFileGroupsOrder = this.mainFileGroups.map(it => it.id);

  public moreFileGroupsWithFiles: FileGroup[] = [];

  private getAllNeededEntities() {
    if (this.requestService.$requestOfflineState() === 'snapshot') {
      void this.restoreSummarySnapshot();

      return;
    }

    combineLatest({
      requestDevices: this.getRequestDevices(),
      requestComments: this.getComments(),
      files: this.loadRequestFiles$(),
      translates: this.getTranslations(),
      fileCats: this.getRequestFileCategories(),
      dealDetails: this.getDealDetails(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(
        ({
          translates,
          files,
          fileCats,
          requestDevices,
          requestComments,
          dealDetails,
        }) => {
          Object.assign(this, {
            translates,
            requestDevices,
            requestComments,
            files,
            fileCats,
            dealDetails,
            isInitialLoad: false,
          });
          this.requestDevicesForInstallQuality$.next(requestDevices);
          this.parseFiles();
          this._$offlineState.set('online');
          void this.saveSummarySnapshot();
        },
        error => void this.restoreSummarySnapshot(error),
      );
  }

  private async saveSummarySnapshot(): Promise<void> {
    const request = this.$request();
    const currentUser = this.accountService.$currentUser();

    if (
      !request ||
      !currentUser ||
      !this.requestDevices ||
      !this.requestComments ||
      !this.files ||
      !this.fileCats
    ) {
      return;
    }

    try {
      await this.offlineSnapshotRepository.saveRequestSummary(
        environment.xOutkeepInfrastructureEnv,
        currentUser.id,
        request.requestNumber,
        {
          request,
          requestDevices: this.requestDevices,
          requestComments: this.requestComments,
          files: this.files,
          fileCategories: this.fileCats,
          dealDetails: this.dealDetails,
        },
      );
    } catch {
      // IndexedDB availability must not break an otherwise successful summary.
    }
  }

  private async restoreSummarySnapshot(error?: unknown): Promise<void> {
    const currentUser = this.accountService.$currentUser();
    const requestNumber = this.getResolvedRequestNumber();

    if (
      !currentUser ||
      !requestNumber ||
      (error && !isOfflineFallbackError(error))
    ) {
      this._$offlineState.set('unavailable');

      return;
    }

    this._$offlineState.set('degraded');
    const snapshot = await this.offlineSnapshotRepository.getRequestSummary(
      environment.xOutkeepInfrastructureEnv,
      currentUser.id,
      requestNumber,
    );

    if (!snapshot) {
      this._$offlineState.set('unavailable');

      return;
    }

    this.applySummarySnapshot(snapshot.payload);
    this._$snapshotFetchedAt.set(snapshot.fetchedAt);
    this._$offlineState.set('snapshot');
  }

  private applySummarySnapshot(snapshot: RequestSummarySnapshot): void {
    Object.assign(this, {
      requestDevices: snapshot.requestDevices,
      requestComments: snapshot.requestComments,
      files: snapshot.files,
      fileCats: snapshot.fileCategories,
      dealDetails: snapshot.dealDetails,
      isInitialLoad: false,
    });
    this.requestDevicesForInstallQuality$.next(null);
    this.parseFiles();
  }

  private parseFiles(): void {
    if (!this.files || !this.fileCats) {
      return;
    }
    const fileMap = new Map<number, RequestFile[]>();
    let ePTS: Nullable<RequestFile> = null;
    let lPPhoto: Nullable<RequestFile> = null;

    // Group files by category and find specific files
    for (const file of this.files) {
      const catId = file.catId as RequestFileCategory;

      if (catId.fileRenamePrefix === 'obrz_pts_psm') {
        ePTS = file;
      } else if (catId.fileRenamePrefix === 'photo_pl') {
        lPPhoto = file;
      }

      if (!fileMap.has(catId.id)) {
        fileMap.set(catId.id, []);
      }
      fileMap.get(catId.id)!.push(file);
    }

    this.ePTS = ePTS;
    this.lPPhoto = lPPhoto;

    // Process main file groups
    for (const group of this.mainFileGroups) {
      const groupFiles = fileMap.get(group.id) || [];

      group.files = groupFiles;
      group.filesExists = groupFiles.length > 0;
      group.multi = groupFiles.length > 1;
    }

    // Sort: groups with files first; ties broken by original declaration order
    this.mainFileGroups.sort(
      (a, b) =>
        +b.filesExists - +a.filesExists ||
        this.mainFileGroupsOrder.indexOf(a.id) -
          this.mainFileGroupsOrder.indexOf(b.id),
    );

    // Process other file categories
    const moreFileGroupsWithFiles: FileGroup[] = [];

    for (const cat of this.fileCats) {
      if (
        !fileMap.has(cat.id) ||
        this.mainFileGroups.some(group => group.id === cat.id)
      ) {
        continue;
      }

      const catFiles = fileMap.get(cat.id)!;

      moreFileGroupsWithFiles.push({
        name: cat.name,
        id: cat.id,
        filesExists: true,
        multi: catFiles.length > 1,
        files: catFiles,
      });
    }
    this.moreFileCatsWithFilesCount = moreFileGroupsWithFiles.length;
    this.moreFileGroupsWithFiles = moreFileGroupsWithFiles;
  }

  private subscribeForLangChange() {
    this.outkeepTranslateService
      .onLangChange()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.locale = this.outkeepTranslateService.getLocaleStr();
      });
  }

  private getTranslations() {
    return this.outkeepTranslateService.translate([
      ...Object.values({
        ...RequestFormFilesDownloadTranslations,
        ...RequestFormDevicesAddDeviceTranslations,
      }),
      RequestFormFilesDownloadTranslations.ChecklistNotFound,
      RequestFormDevicesAddDeviceTranslations.DialogAddDevice,
      RequestFormDevicesAddDeviceTranslations.DialogAddDeviceYes,
      RequestFormDevicesAddDeviceTranslations.DialogAddDeviceNo,
      RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDateYes,
      RequestFormDevicesAddDeviceTranslations.DialogConfirmReleaseDateNo,
      WorkflowsTranslations.StartWorkFlowExecution,
      WorkflowsTranslations.ContinueWorkFlowExecution,
    ]);
  }

  private getRequestFileCategories() {
    const counterpartyId = this.$request()?.counterpartyOwnerId?.id;

    return this.requestFilesService.getRequestFileCategories$(
      counterpartyId ? [counterpartyId] : [],
    );
  }

  private getRequestDevices() {
    if (this.needToResetEntitiesCache()) {
      this.requestDevicesService.resetCache();
    }

    return this.requestDevicesService.entities$;
  }

  private getDealDetails() {
    return this.dealDetailsService.requestDealDetails$;
  }

  private getComments() {
    if (this.needToResetEntitiesCache()) {
      this.requestCommentsService.resetCache();
    }

    return this.requestCommentsService.entities$;
  }

  private loadRequestFiles$() {
    if (this.needToResetEntitiesCache()) {
      this.requestFilesService.resetCache();
    }

    return this.requestFilesService.entities$;
  }

  public formatDateWithLocale(stringDate?: string | null): string {
    if (stringDate) {
      // Map locale string to date-fns locale
      const localeMap = {
        'ru-RU': ru,
        'tr-TR': tr,
        'en-Gb': enGB,
      };

      const locale = localeMap[this.locale as keyof typeof localeMap] || ru;

      return formatInTimeZone(
        new Date(stringDate),
        'Europe/Moscow',
        "EEEE d MMMM yyyy'г.'",
        { locale },
      );
    } else {
      return '';
    }
  }

  public shareRequestLink(): void {
    const request = this.$request()!;
    const shareData = {
      title: request!.contract || request.requestNumber,
      text: `Номер заявки: ${request.requestNumber}\nНомер проекта: ${request.projectNumber}\nДоговор: ${request.contract}\nСтатус: ${request.statusId!.name}\nСоздана: ${this.formatDateWithLocale(request.releaseDate)}\nЗаказчик: ${request.counterPartyName}\nVIN: \`\`\`${request.vin}\`\`\`\nУстановщик: ${request.installerShort} ${this.lPPhoto ? `\nФото ПЛ: ${this.location.origin + environment.downloadFilesUrl + this.lPPhoto.path}` : ''} ${this.ePTS ? `\nЕ-ПТС: ${this.location.origin + environment.downloadFilesUrl + this.ePTS.path}` : ''}\nURL:\`${this.location.origin}/requests/${request.id}\``,
      url: `${this.location.href}`,
    };

    void navigator.share(shareData);
  }

  public getCheckList() {
    if (this.$isReadOnly()) {
      return;
    }
    this.requestSettingsService
      .getCheckList$(this.$request()!.counterpartyOwnerId?.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(response => {
        if ('fullPath' in response && response.fullPath) {
          const filename = response.fileName || 'checklist';

          if (this.fileDownloadService.isPdfFile(filename, response.fullPath)) {
            this.fileDownloadService.openPdfByUrl(response.fullPath, filename);

            return;
          }

          this.fileDownloadService.downloadFile(
            response.fullPath,
            filename,
            false,
            this.renderer,
          );
        } else {
          this.xAlertService.error(
            this.translates[
              RequestFormFilesDownloadTranslations.ChecklistNotFound
            ]!,
          );
        }
      });
  }

  public openYaMaps() {
    if (this.$isReadOnly()) {
      return;
    }
    this.window.open(
      `https://maps.yandex.ru/?text=${this.$request()!.deliveryAddress}&l=sat%2Cskl`,
      '_blank',
    );
  }

  private needToResetEntitiesCache() {
    // этот костыль нужен для случаев когда пользователь нажимает на кнопку "Назад" в браузере,
    // а потом снова переходит на страницу заявки - в этом случае все entities сервисы ломаются и не видят, что айди заявки не совпадает с текущим
    const previousPath = this.previousUrl?.split('?')[0];

    return !previousPath || REQUEST_LIST_ROUTE_PATHS.includes(previousPath);
  }
}
